'use client';

import { easings, useReducedMotion, useTransition } from '@react-spring/web';
import { type ReactElement, cloneElement, forwardRef, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useToast } from './hooks/use-toast';
import { toastProviderListRecipe } from './styles/toast-provider-list.styles';
import { ToastProviderItem } from './toast-provider-item';
import type { ToastProviderListPropsInterface } from './types/toast-provider-list.types';

/**
 * ToastProvider.List ingests toasts from context and renders them in a list
 */
export const ToastProviderList = forwardRef<HTMLUListElement, ToastProviderListPropsInterface>(
  (props: ToastProviderListPropsInterface, forwardedRef) => {
    const {
      className,
      css: cssProp,
      renderToast = (toast) => <ToastProviderItem key={toast.id} toast={toast} />,
      ...rest
    } = props;
    const { placement, toasts } = useToast();

    const toastRefMap = useMemo(() => new Map<number, HTMLLIElement>(), []);

    const reduceMotion = useReducedMotion();

    const transitions = useTransition(toasts, {
      keys: (toast) => toast.id,
      enter: (toast) => async (next) => {
        const height = toastRefMap.get(toast.id)?.scrollHeight ?? 'auto';
        await next({ opacity: 1, height: height });
      },
      from: { opacity: 0, height: 0 },
      leave: { opacity: 0, height: 0 },
      config: {
        easing: easings.easeInOutQuart,
        duration: 450,
      },
    });

    const styles = css(toastProviderListRecipe.raw({ placement }), cssProp);

    // Check for reduce motion to return static or animated toasts
    const content = useMemo(
      () =>
        reduceMotion
          ? toasts.map((toast) =>
              cloneElement(renderToast(toast) as ReactElement, {
                ref: (ref: HTMLLIElement) => ref && toastRefMap.set(toast.id, ref),
              }),
            )
          : transitions((style, toast) =>
              cloneElement(renderToast(toast) as ReactElement, {
                style,
                ref: (ref: HTMLLIElement) => ref && toastRefMap.set(toast.id, ref),
              }),
            ),
      [renderToast, toasts, transitions, reduceMotion, toastRefMap],
    );

    return (
      <ul
        ref={forwardedRef}
        className={cx('c11n', styles, className)}
        data-c11n-component="ToastProvider.List"
        data-c11n-version={__C11N_VERSION__}
        {...rest}
      >
        {content}
      </ul>
    );
  },
);

ToastProviderList.displayName = 'ToastProvider.List';
